import MapboxDraw, { DrawCustomModeThis, DrawFeature } from "@mapbox/mapbox-gl-draw";
import { expression } from "@mapbox/mapbox-gl-style-spec";
import { Feature } from "geojson";
import { Utils } from "mapbox-gl-draw-snap-mode";

const {
  constants: Constants,
  lib: {
    CommonSelectors: { isOfMetaType },
  },
} = MapboxDraw;

const isVertex = isOfMetaType(Constants.meta.VERTEX);
const isMidpoint = isOfMetaType(Constants.meta.MIDPOINT);

/**
 * Evaluates a Mapbox filter expression against a feature.
 * Returns true if the feature matches the filter, false otherwise.
 * Silently handles evaluation errors (e.g., null values) by returning false.
 *
 * @param filterExpression - The Mapbox filter expression to evaluate
 * @param feature - The GeoJSON feature to test against the filter
 * @returns true if the feature matches the filter, false otherwise
 */
export function evaluateFilterExpression(filterExpression: unknown[], feature: Feature): boolean {
  try {
    const expr = expression.createExpression(filterExpression, { type: "boolean" });
    if (expr.result === "success") {
      try {
        return expr.value.evaluate({ zoom: 0 }, feature);
      } catch (evalError) {
        // Evaluation failed (e.g., null value where number expected)
        // Exclude feature from sublayer
        return false;
      }
    } else {
      return false;
    }
  } catch (error) {
    return false;
  }
}

export interface ColocatedFeature {
  id: string;
  type: "Point" | "LineString" | "Polygon";
  vertexIndex?: number; // Only for LineString
  ringIndex?: number; // Only for Polygon
  coordIndex?: number; // Only for Polygon
}

/**
 * Checks if a DrawFeature has a point or vertex at the specified location.
 * Used for sticky selection - prioritizing already-selected features at a click location.
 *
 * @param feature - The DrawFeature to check
 * @param point - The coordinates to check [lng, lat]
 * @param tolerance - The tolerance for coordinate matching (default: 0.000001)
 * @returns true if the feature has a point/vertex at the location
 */
export function isFeatureAtLocation(feature: DrawFeature, point: [number, number], tolerance = 0.000001): boolean {
  if (feature.type === "Point") {
    const coords = feature.getCoordinates() as [number, number];
    return Math.abs(coords[0] - point[0]) < tolerance && Math.abs(coords[1] - point[1]) < tolerance;
  } else if (feature.type === "LineString") {
    const coords = feature.getCoordinates() as Array<[number, number]>;
    return coords.some((c) => Math.abs(c[0] - point[0]) < tolerance && Math.abs(c[1] - point[1]) < tolerance);
  } else if (feature.type === "Polygon") {
    const coords = feature.getCoordinates() as Array<Array<[number, number]>>;
    return coords.some((ring) => ring.some((c) => Math.abs(c[0] - point[0]) < tolerance && Math.abs(c[1] - point[1]) < tolerance));
  }

  return false;
}

/**
 * Determines which feature should be selected at a click location using sticky selection logic.
 *
 * Sticky selection rules:
 * 1. Select the topmost feature by default (Mapbox's default behavior)
 * 2. BUT if there's already a selected feature/vertex at that location, prioritize it
 *
 * This allows users to continue interacting with already-selected features even when
 * they're colocated with features from other layers.
 *
 * When editing a sublayer, also filters by the sublayer's filter expression.
 *
 * Note that this relies on features having a `layerId` property which is set when
 * the feature is created.
 */
export function getFeatureTarget(
  this: DrawCustomModeThis,
  originalEvent: MapboxDraw.MapMouseEvent,
  opts: MapboxDraw.MapboxDrawOptions
): Feature | undefined {
  const editOptions = opts.editOptions;

  if (editOptions?.editingLayerIds && editOptions.editingLayerIds.length > 0) {
    // Get all features at the click location that are from editable layers
    const features = this.featuresAt(originalEvent, null).filter((feature) => {
      // @ts-expect-error featureTarget is defined as MapboxDraw.DrawFeature although it is a GeoJSON Feature.
      const event = { ...originalEvent, featureTarget: feature } as MapboxDraw.MapMouseEvent;

      // Get the feature ID - if it's a vertex or midpoint, get the parent feature ID
      const featureId = isVertex(event) || isMidpoint(event) ? feature.properties?.parent : feature.properties?.id;
      if (!featureId) return false;

      // Get the actual DrawFeature to access properties without user_ prefix
      const drawFeature = this.getFeature(featureId);
      if (!drawFeature) return false;

      const featureTarget = drawFeature.toGeoJSON() as Feature;

      // Only include features from editable layers
      const layerId = featureTarget?.properties?.layerId;
      if (!layerId || !editOptions.editingLayerIds.includes(layerId)) return false;

      // If we're editing a sublayer with a filter, apply the filter expression
      if (opts.sublayerFilter && opts.sublayerFilter.length > 0) {
        return evaluateFilterExpression(opts.sublayerFilter, featureTarget);
      }

      return true;
    });

    if (features.length === 0) return undefined;

    // Sticky selection: check if any currently selected feature is at this location
    const selectedFeatures = this.getSelected();
    if (selectedFeatures.length > 0) {
      const clickedPoint = [originalEvent.lngLat.lng, originalEvent.lngLat.lat] as [number, number];

      // Check if any selected feature has a point/vertex at the clicked location
      for (const selected of selectedFeatures) {
        const matchingFeature = features.find((f) => {
          const fId = f.properties?.id || f.properties?.parent;
          return fId === selected.id;
        });

        if (matchingFeature && isFeatureAtLocation(selected, clickedPoint)) {
          return matchingFeature;
        }
      }
    }

    // No selected feature at this location, return topmost (Mapbox default behavior)
    return features[0];
  }

  // If no editingLayerIds are set then just return the original featureTarget.
  // @ts-expect-error featureTarget is defined as MapboxDraw.DrawFeature although it is a GeoJSON Feature.
  return originalEvent.featureTarget;
}

/**
 * Removes the horizontal and vertical snapping guides. These guides are added during the setup of the snap modes but we don't use them in our implementation so we remove them to avoid having to delete them later.
 * @param this
 */
export function removeGuides(this: DrawCustomModeThis): void {
  this.deleteFeature(Utils.IDS.HORIZONTAL_GUIDE, { silent: true });
  this.deleteFeature(Utils.IDS.VERTICAL_GUIDE, { silent: true });
}

/*
 * Find all features colocated at a specific coordinate location.
 * Checks Points at the exact location, and LineString/Polygon vertices at the location.
 *
 * @param vertexCoords - The coordinates to check for colocation
 * @param nearbyFeatures - Features near the location (from featuresAt query)
 * @param excludeFeatureId - Feature ID to exclude from results (typically the feature being dragged)
 * @param allowedLayerIds - Optional array of layer IDs to filter by (only features from these layers will be included)
 * @param getFeature - Function to get the actual DrawFeature with current coordinates
 * @returns Array of colocated features with their type and vertex indices
 */
export function findColocatedFeaturesAtLocation(
  vertexCoords: [number, number],
  nearbyFeatures: Feature[],
  excludeFeatureId: string | number,
  allowedLayerIds: string[] | undefined,
  getFeature: (id: string | number) => DrawFeature | undefined
): ColocatedFeature[] {
  const colocated: ColocatedFeature[] = [];

  nearbyFeatures.forEach((f) => {
    // Exclude the current feature and only check features with IDs
    if (!f.properties?.id || f.properties.id === excludeFeatureId) return;

    // Filter by layer ID if allowedLayerIds is provided
    if (allowedLayerIds && allowedLayerIds.length > 0) {
      const featureLayerId = f.properties.layerId || f.properties.user_layerId;
      if (!featureLayerId || !allowedLayerIds.includes(featureLayerId)) return;
    }

    const fId = f.properties.id;

    // Get the actual DrawFeature to ensure we have current coordinates
    const drawFeature = getFeature(fId);
    if (!drawFeature) return;

    if (drawFeature.type === "Point") {
      const coords = drawFeature.getCoordinates() as [number, number];
      // Check if coordinates match (within a small tolerance)
      if (Math.abs(coords[0] - vertexCoords[0]) < 0.000001 && Math.abs(coords[1] - vertexCoords[1]) < 0.000001) {
        // Check if not already added
        if (!colocated.some((c) => c.id === fId && c.type === "Point")) {
          colocated.push({ id: fId, type: "Point" });
        }
      }
    } else if (drawFeature.type === "LineString") {
      // For LineStrings, check each vertex
      const coords = drawFeature.getCoordinates() as Array<[number, number]>;
      coords.forEach((vertexCoord, index) => {
        if (Math.abs(vertexCoord[0] - vertexCoords[0]) < 0.000001 && Math.abs(vertexCoord[1] - vertexCoords[1]) < 0.000001) {
          // Check if not already added
          if (!colocated.some((c) => c.id === fId && c.type === "LineString" && c.vertexIndex === index)) {
            colocated.push({ id: fId, type: "LineString", vertexIndex: index });
          }
        }
      });
    } else if (drawFeature.type === "Polygon") {
      // For Polygons, check each vertex in each ring
      const coords = drawFeature.getCoordinates() as Array<Array<[number, number]>>;
      coords.forEach((ring, ringIndex) => {
        const lastIndex = ring.length - 1;
        ring.forEach((vertexCoord, coordIndex) => {
          if (Math.abs(vertexCoord[0] - vertexCoords[0]) < 0.000001 && Math.abs(vertexCoord[1] - vertexCoords[1]) < 0.000001) {
            // Skip the last vertex if it's the same as first (closure point)
            // We'll handle closure when moving the first vertex
            if (coordIndex === lastIndex && lastIndex > 0) {
              return;
            }

            // Check if not already added
            if (!colocated.some((c) => c.id === fId && c.type === "Polygon" && c.ringIndex === ringIndex && c.coordIndex === coordIndex)) {
              colocated.push({ id: fId, type: "Polygon", ringIndex, coordIndex });
            }
          }
        });
      });
    }
  });

  return colocated;
}

/**
 * Move all colocated features by the specified delta.
 * For Points, moves the entire point.
 * For LineStrings and Polygons, only moves the specific colocated vertex.
 *
 * @param colocatedFeatures - Array of colocated features to move
 * @param delta - Movement delta (lng, lat)
 * @param getFeature - Function to retrieve a feature by ID
 */
export function moveColocatedFeatures(
  colocatedFeatures: ColocatedFeature[],
  delta: { lng: number; lat: number },
  getFeature: (id: string | number) => DrawFeature | undefined
): void {
  colocatedFeatures.forEach((colocated) => {
    const feature = getFeature(colocated.id);
    if (!feature) return;

    if (colocated.type === "Point") {
      // Move the entire point
      const coords = feature.getCoordinates() as [number, number];
      feature.updateCoordinate("", coords[0] + delta.lng, coords[1] + delta.lat);
    } else if (colocated.type === "LineString" && colocated.vertexIndex !== undefined) {
      // Move only the specific vertex
      const coords = feature.getCoordinates() as Array<[number, number]>;
      feature.updateCoordinate(
        colocated.vertexIndex.toString(),
        coords[colocated.vertexIndex][0] + delta.lng,
        coords[colocated.vertexIndex][1] + delta.lat
      );
    } else if (colocated.type === "Polygon" && colocated.ringIndex !== undefined && colocated.coordIndex !== undefined) {
      // Move only the specific vertex in the specific ring
      const { ringIndex, coordIndex } = colocated;
      const coordPath = `${ringIndex}.${coordIndex}`;

      // Get the current coordinate value using getCoordinate (like mapbox-gl-draw does internally)
      const currentCoord = feature.getCoordinate(coordPath) as [number, number];
      const newLng = currentCoord[0] + delta.lng;
      const newLat = currentCoord[1] + delta.lat;

      // Update the specific vertex using coordinate path
      feature.updateCoordinate(coordPath, newLng, newLat);
    }
  });
}

/**
 * Filters features that can be edited (moved, deleted, modified) based on multi-layer editing configuration.
 *
 * When multi-layer editing is enabled (allLayers is defined):
 * - Features from regular layers: layerId must be in editingLayerIds
 * - Features from sublayers: layerId must match an editable sublayer's parent and pass its filter
 * - All features from editingLayerIds can be edited simultaneously
 *
 * When multi-layer editing is NOT enabled (allLayers is undefined):
 * - All features can be edited (default behavior)
 *
 * @param options - MapboxDraw options containing editOptions configuration
 * @param features - Array of features to filter
 * @returns Filtered array of features that can be edited
 */
export function filterEditableFeatures(options: MapboxDraw.MapboxDrawOptions, features: Feature[]): Feature[] {
  const { editOptions, parentLayerId, sublayerFilter } = options;
  const allLayers = editOptions?.allLayers;
  const editingLayerIds = editOptions?.editingLayerIds;
  const editableSublayers = editOptions?.editableSublayers || [];

  // If multi-layer editing is enabled, filter based on editingLayerIds
  if (allLayers && allLayers.length > 0) {
    return features.filter((f) => {
      const featureLayerId = f.properties?.layerId;
      if (!featureLayerId || !editingLayerIds) return false;

      // Check if this is a regular layer (directly in editingLayerIds)
      if (editingLayerIds.includes(featureLayerId)) {
        // Regular layer: feature is editable
        return true;
      }

      // Check if this feature belongs to an editable sublayer
      // Find editable sublayers that match this feature's parent layer
      const matchingSublayers = editableSublayers.filter((sub) => sub.parentLayerId === featureLayerId);

      if (matchingSublayers.length === 0) {
        return false; // No editable sublayers for this parent layer
      }

      // Feature must be from a currently editable sublayer
      if (parentLayerId) {
        // We're currently editing a sublayer - apply the sublayer's filter
        if (sublayerFilter && sublayerFilter.length > 0) {
          return evaluateFilterExpression(sublayerFilter, f);
        }
        return true;
      }

      // Not currently editing a sublayer, so feature is not editable
      return false;
    });
  }

  // Default behavior: all features can be edited
  return features;
}
